/**
 * The Kapitan panel's own frame (§2.3, §4e): a dark band, its sections, and nothing of an agency's
 * shell. Sections are links in a rail on a wide screen and a scrolling row of pills on a phone.
 */
import { useEffect, type JSX } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";

import { useTranslation, type TranslationKey } from "@/i18n";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/auth";
import { Toaster } from "@/ui/toast";

const SECTIONS: readonly { to: string; label: TranslationKey }[] = [
  { to: "/platforma/tenantlar", label: "plat.nav.tenants" },
  { to: "/platforma/tenant-yaratish", label: "plat.nav.create" },
  { to: "/platforma/support", label: "plat.nav.support" },
  { to: "/platforma/provayderlar", label: "plat.nav.providers" },
  { to: "/platforma/kanallar", label: "plat.nav.channels" },
  { to: "/platforma/ai-sarfi", label: "plat.nav.ai" },
  { to: "/platforma/tariflar", label: "plat.nav.billing" },
  { to: "/platforma/kataloglar", label: "plat.nav.catalogues" },
  { to: "/platforma/otel-normalizatsiya", label: "plat.nav.hotels" },
  { to: "/platforma/demo-sorovlar", label: "plat.nav.leads" },
  { to: "/platforma/kredit-sorovlari", label: "plat.nav.credits" },
  { to: "/platforma/audit", label: "plat.nav.audit" },
  { to: "/platforma/aeroportlar", label: "plat.nav.airports" },
  { to: "/platforma/yonalishlar", label: "plat.nav.destinations" },
  { to: "/platforma/tizim", label: "plat.nav.settings" },
  { to: "/platforma/integratsiyalar", label: "plat.nav.system" },
];

export function PlatformShell(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const name = useAuthStore((state) => state.user?.full_name ?? "");
  const logout = useAuthStore((state) => state.logout);

  useEffect(() => {
    document.documentElement.classList.add("k-platform-scope");
    return (): void => {
      document.documentElement.classList.remove("k-platform-scope");
    };
  }, []);

  return (
    <div className="flex h-dvh flex-col bg-[var(--k-platform-page)]">
      <header className="flex h-14 shrink-0 items-center gap-3 bg-[var(--k-platform-band)] px-4 text-surface">
        <span aria-hidden="true" className="grid size-7 shrink-0 place-items-center rounded-small bg-accent text-base font-semibold text-surface">K</span>
        <span className="whitespace-nowrap text-lg font-semibold">{t("plat.shell.title")}</span>
        <span className="hidden whitespace-nowrap text-xs text-[var(--k-platform-band-muted)] sm:inline">/platform</span>
        <span className="ml-auto flex min-w-0 items-center gap-2.5 text-sm">
          <span className="hidden truncate text-[var(--k-platform-band-muted)] md:inline">
            {t("plat.shell.who", { name })}
          </span>
          <button
            type="button"
            onClick={() => {
              logout();
              void navigate("/kirish");
            }}
            className="whitespace-nowrap rounded-small border border-[var(--k-platform-band-muted)] px-2.5 py-1 text-surface hover:bg-[var(--k-platform-accent)]"
          >
            {t("plat.shell.exit")}
          </button>
        </span>
      </header>
      <nav
        aria-label={t("plat.shell.sections")}
        className="flex shrink-0 gap-1.5 overflow-x-auto border-b border-[var(--k-platform-line)] bg-surface px-3 py-2.5 md:hidden"
      >
        {SECTIONS.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            className={({ isActive }) =>
              cn(
                "inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-pill px-3 text-sm",
                isActive
                  ? "bg-[var(--k-platform-band)] font-semibold text-surface"
                  : "border border-[var(--k-platform-line)] bg-surface text-[var(--k-platform-nav)]",
              )
            }
          >
            {t(section.label)}
          </NavLink>
        ))}
      </nav>
      <div className="flex min-h-0 flex-1">
        <nav
          aria-label={t("plat.shell.sections")}
          className="hidden w-56 shrink-0 flex-col gap-0.5 overflow-auto border-r border-[var(--k-platform-line)] bg-surface px-2 py-3 md:flex"
        >
          {SECTIONS.map((section) => (
            <NavLink
              key={section.to}
              to={section.to}
              className={({ isActive }) =>
                cn(
                  "rounded-control px-3 py-2 text-base",
                  isActive
                    ? "bg-[var(--k-platform-active)] font-semibold text-[var(--k-platform-text)]"
                    : "text-[var(--k-platform-nav)] hover:bg-[var(--k-platform-active)]",
                )
              }
            >
              {t(section.label)}
            </NavLink>
          ))}
        </nav>
        <main className="min-w-0 flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
      <Toaster />
    </div>
  );
}
